import { changeMode } from "@/store/mode-slice";
import { useAppDispatch, useAppStore } from "@/store/store-hooks";
import {
  IElement,
  IElementGenericPointCloudStream,
  isIElementGenericPointCloudStream,
} from "@faro-lotv/ielement-types";
import { useCallback, useMemo } from "react";
import {
  useComputeValidModeElement,
  useCurrentArea,
} from "../mode-data-context";
import { selectIsElementViewable } from "../mode-selectors";

/**
 * Analyze the current scene and adjust it so it can work for export mode.
 * We mainly need this, because useCurrentScene can also return panos of individual scans for a given data set.
 *
 * @param customFallback - An optional custom fallback function to be called if no valid point cloud is found
 * @returns A point cloud to use in the export mode
 */
export function useComputeValidExportModeScene(
  customFallback?: () => void,
): IElementGenericPointCloudStream | undefined {
  const { dataSessions3d, dataSessions5d } = useCurrentArea();

  const dispatch = useAppDispatch();
  const { getState } = useAppStore();

  const validSessions = useMemo(
    () => [...dataSessions3d.pointClouds, ...dataSessions5d],
    [dataSessions3d, dataSessions5d],
  );

  const computeValidModel = useCallback(
    (el: IElement): el is IElementGenericPointCloudStream =>
      isIElementGenericPointCloudStream(el) &&
      selectIsElementViewable(el)(getState()),
    [getState],
  );

  const fallback = useCallback(() => {
    dispatch(changeMode("sheet"));
  }, [dispatch]);

  return useComputeValidModeElement(
    validSessions,
    computeValidModel,
    customFallback ?? fallback,
  );
}
